import { getCurrentLanguage } from "@/core/i18n";
import {
  getAuthClaims,
  getGiftCards,
  getMemberships,
  getPlatform,
  getPlatformLocation,
  getPlatformTheme,
  getPromotions,
  getRewards,
  getShopCustomCategoriesListings,
  getShopDiscounts,
  getShopMemberships,
  getUser,
} from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import type { CatalogHit } from "@/utils/algoliaCatalog";
import type { CatalogSummary } from "hooks/useCatalogSummary";
import { recommendationIdsQuery } from "hooks/useRecommendedCatalogHits";
import { Analytics } from "modules/analytics";
import { isAnonymousUser } from "modules/anonymousBrowsing";
import { prefetchContent } from "modules/routing/contentQueries";
import { queryClient } from "pages/Root";
import { getDeviceInfo } from "utils/device.helpers";

/**
 * Everything /home renders, as individual queries rather than one composite.
 *
 * No routing decision on this page turns on any of them — /home is where the other routes redirect
 * *to* — so nothing here is worth making the patient wait behind. The requests start at navigation
 * intent through `prefetchHomePage`, and each band of the page reads its own key back through
 * `useQuery`, so no single endpoint decides when the rest of the screen appears. The best-selling
 * rail, the retail rail and the "browse by concern" gate read the Algolia catalog summary
 * (`useCatalogSummary`) instead of `GET /api/v1/listings`, which was 2,154ms at p95.
 */
export const homeContentQueries = {
  platform: () => ({ ...getPlatform.keyed(), staleTime: STALE_TIME.platformConfig }),
  userInfo: () => ({ ...getUser.keyed(), staleTime: 1000 * 60 * 2 }),
  rewards: () => ({ ...getRewards.keyed({}) }),
  membershipFeatures: () => ({ ...getShopMemberships.keyed() }),
  memberships: () => ({ ...getMemberships.keyed() }),
  promotions: () => ({ ...getPromotions.keyed(), staleTime: 1000 * 60 * 2 }),
  platformLocation: () => ({
    ...getPlatformLocation.keyed(),
    staleTime: STALE_TIME.platformConfig,
  }),
  theme: () => ({ ...getPlatformTheme.keyed(), staleTime: STALE_TIME.platformConfig }),
  customCategoriesList: () => ({
    ...getShopCustomCategoriesListings.keyed(),
    queryFn: () =>
      getShopCustomCategoriesListings().then((listings) =>
        listings.filter(
          (listing) =>
            listing.promotion?.expiresOn == null ||
            new Date(listing.promotion.expiresOn) > new Date(),
        ),
      ),
    staleTime: 1000 * 10,
  }),
  shopDiscounts: () => ({ ...getShopDiscounts.keyed(), staleTime: 1000 * 10 }),
  giftCardListings: () => ({
    ...getGiftCards.keyed({ FilterActiveOnly: true }),
    staleTime: 1000 * 10,
  }),
} as const;

/**
 * How many recommendations the home band shows. The ids 401 for a signed-out visitor; an absent set
 * means no recommendation band, so they are read on their own rather than as part of the gate.
 */
export const HOME_RECOMMENDATION_LIMIT = 5;

/** Identify runs once per signed-in patient per app boot; the page may prefetch more than once. */
let identifiedUserId: string | null = null;

const identifyPatient = async () => {
  const [user, { claims }] = await Promise.all([
    queryClient.fetchQuery(homeContentQueries.userInfo()),
    queryClient.fetchQuery({ ...getAuthClaims.keyed() }),
  ]);
  if (isAnonymousUser(user) || identifiedUserId === user.id) return;
  identifiedUserId = user.id;

  const deviceInfo = await getDeviceInfo();

  Analytics.identify(user.id, {
    firstName: user.firstName,
    lastName: user.lastName,
    phoneNumber: user.phoneNumber || "",
    email: user.email || "",
    subdomain: user.platformDomain?.domain || "",
    platformId: user.platformDomain?.platformId || "",
    platformUserId: claims.platformUserId,
    role: "customer",
    language: getCurrentLanguage(),
    environment: import.meta.env.MODE,
    version: "v2",
    ...deviceInfo,
  });
};

export const prefetchHomePage = () => {
  const content = prefetchContent(homeContentQueries);
  void queryClient.prefetchQuery(recommendationIdsQuery(HOME_RECOMMENDATION_LIMIT));

  identifyPatient().catch(() => {
    console.error("Failed to identify the analytics user");
  });

  return content;
};

export type HomePageData = {
  platform: NonNullable<Awaited<ReturnType<typeof getPlatform>>>;
  userInfo: Awaited<ReturnType<typeof getUser>>;
  rewards: Awaited<ReturnType<typeof getRewards>>;
  membershipFeatures: Awaited<ReturnType<typeof getShopMemberships>>;
  memberships: Awaited<ReturnType<typeof getMemberships>>;
  catalog: CatalogSummary;
  promotions: Awaited<ReturnType<typeof getPromotions>>;
  platformLocation: Awaited<ReturnType<typeof getPlatformLocation>>;
  theme: Awaited<ReturnType<typeof getPlatformTheme>>;
  customCategoriesList: Awaited<ReturnType<typeof getShopCustomCategoriesListings>>;
  shopDiscounts: Awaited<ReturnType<typeof getShopDiscounts>> | undefined;
  giftCardListings: Awaited<ReturnType<typeof getGiftCards>>;
  recommendations: CatalogHit[];
};
